/**
 * 普通对象
 */
export interface PlainObject {
  [key: string]: string | number | boolean | undefined | null | Function | string[] | Error;
}

export type FilterEventFn<T> = (info: T) => boolean;

/**
 * 上报级别
 */
export enum EventLevel {
  INFO = 'info', // 消息在粗粒度级别上突出强调应用程序的运行过程
  WARN = 'warn', // 表明会出现潜在错误的情形 有些信息不是错误信息 但是也要给程序员的一些提示
  ERROR = 'error', // 发生错误事件，可能影响系统的正常运行
}

/**
 * 错误类型
 */
export enum ErrorType {
  JsError = 'JsError', // js报错
  UnhandledRejection = 'UnhandledRejection', // 未捕获的promise报错
  ManualHandleJsError = 'ManualHandleJsError', // 手动处理上报的Js 错误
}

/**
 * 事件名称
 */
export enum EventIds {
  BtnClick = 'BtnClick', // 点击事件
  FunctionEvent = 'FunctionEvent', // 其他功能事件
  DialogExposure = 'DialogExposure', // 弹窗曝光事件
  PageView = 'PageView', // 页面曝光
  PageLeave = 'PageLeave', // 页面离开
  ModuleExposure = 'ModuleExposure', // 模块曝光
  RequestInfo = 'RequestInfo', // 请求事件
  WebPerformance = 'WebPerformance', // 性能上报
  ErrorInfo = 'ErrorInfo', // 错误上报
  BlankScreen = 'BlankScreen', // 白屏错误上报
  LoadBlankError = 'LoadBlankError', // Load事件触发时判断的白屏
  ResourceErrorInfo = 'ResourceErrorInfo', // 资源错误上报
  PageNotFound = 'PageNotFound', // 页面404事件
  CustomError = 'CustomError', // 自定义错误
}

export interface BaseEvent extends PlainObject {
  /** 错误code */
  event: EventIds;
  event_level?: EventLevel | '';
  micro_app?: string;
}

/**
 * 错误对象描述声明
 */
export interface ErrorInfo extends BaseEvent {
  type: ErrorType; //  ERROR_TYPE
  message: string; // 错误描述
  filename?: string; // 错误的源文件
  lineno?: number; // 行号
  colno?: number; // 列号
  stack_list?: string[]; // 错误堆栈
  error_code?: string; // 自定义错误code，自定义错误必传
  error_key?: string; // 错误编码
}

/**
 * 资源错误对象描述声明
 */
export interface ResourceErrorInfo extends BaseEvent {
  filename: string; // 资源地址
  tag: string; // 标签类型
  attrs: string; // dom属性
  element_path: string;
  element_selector: string;
  is_online: number;
}

/**
 * 模块曝光事件
 */
export interface ModuleExposureEvent extends BaseEvent {
  event_code: string;
  event_name?: string;
  visible?: boolean;
  duration?: number;
}

/**
 * 模块曝光observer事件
 */
export interface ModuleExposureObserverEvent {
  event_code: string;
  event_name?: string;
  visible?: boolean;
  duration?: number;
  params?: object;
  target?: Element;
}

/**
 * 请求类型
 */
export enum RequestType {
  ok = 'ok', // 请求正常,响应码http_status==200,success===true
  server_error = 'server_error', // 服务器响应错误，响应码http_status!=200
  business_error = 'business_error', // 业务错误，响应码http_status==200,success!==true
  json_error = 'json_error', // 业务错误，响应码http_status==200,json响应，但是json解析失败
  network_error = 'network_error', // 网络错误,无网络
  abort = 'abort', // 请求取消，中断
  timeout = 'timeout', // 请求超时
}

export interface RequestInfo extends BaseEvent {
  type: RequestType; // 请求类型
  traceid: string; // 请求唯一id，随机生成
  http_url: string; // 接口url，不包括query参数
  http_path: string; // 接口path
  http_query: string; // 请求参数
  http_params: string; // 请求body参数
  http_status: string; // http响应状态码
  http_method: string; // get、post、put等
  response_body?: string; // 响应,只有json类型才会上报
  // 大数据规范：
  // 0代表网络异常、1代表业务异常。
  // 网络异常定义：http返回400、500等。
  // 业务异常定义：返回200，但接口、业务逻辑有问题，后端返回信息缺失参数。
  error_type: string;
  error_message?: string; // 接口错误描述
  error_code?: string; // 接口错误描述
  duration: number; // 时长
  start_time: number;
  end_time: number;
  sw8: string;
}

export interface PerformanceEvent extends BaseEvent {
  cls_time?: number; // 累积布局偏移：测量视觉稳定性。为了提供良好的用户体验，页面的 CLS 应保持在 0.1. 或更少
  fid_time?: number; // 首次输入延迟：测量交互性。为了提供良好的用户体验，页面的 FID 应为100 毫秒或更短
  lcp_time?: number; // 最大内容绘制：测量加载性能。为了提供良好的用户体验，LCP 应在页面首次开始加载后的2.5 秒内发生。
  custom_lcp_time?: number; // 自定义最大内容绘制
  unload_time?: number; // 上一个页面卸载时间
  redirect_time?: number; // 重定向时间
  appcache_time?: number; // appcache
  dns_time?: number; // DNS解析，DNS查询耗时
  tcp_time?: number; // tcp连接耗时
  ssl_time?: number; // ssl
  req_time?: number; // request请求耗时
  waiting_ttfb_time?: number; // waiting(TTFB):等待第一个字节时间
  ttfb_time?: number; // TTFB:读取页面第一个字节的时间
  fp_time?: number; // FP:白屏时间,此时页面资源加载完成，即将进入渲染环节
  dom_time?: number; // 解析dom树耗时
  fcp_time?: number; // FCP:首屏时间,此时页面DOM树已经解析完成并且显示内容
  load_time?: number; // 从开始至load总耗时,load事件调用时间，这个时候vue dom渲染完成
  support_webp?: boolean; // 是否支持webp
}

export interface PageViewEvent extends BaseEvent {
  url_path: string; // 页面链接url
  visible: boolean; // 页面是否显示，true=页面访问,false=页面离开
  duration?: number; // 页面访问时长，只有页面离开有值
}

export type EventHandler<T> = (event: T) => void;

export type NavigationTimingPolyfillEntry = Omit<
  PerformanceNavigationTiming,
  | 'initiatorType'
  | 'nextHopProtocol'
  | 'redirectCount'
  | 'transferSize'
  | 'encodedBodySize'
  | 'decodedBodySize'
  | 'toJSON'
>;

/**
 * 平台环境
 */
export enum PlatformEnv {
  dev = 'dev', // 本地开发
  sit = 'sit', // 测试
  pre = 'pre', // 预发布
  prod = 'prod', // 生产环境
}
